import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/seo/SEOHead";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, Tag } from "lucide-react";
import {
  blogIndexPath,
  blogPostPath,
  formatBlogDate,
  resolveDocumentTitle,
  resolveMetaDescription,
} from "@/lib/blog/constants";
import { BLOG_ARTICLE_PROSE_CLASS, BLOG_HERO_GRADIENT } from "@/lib/blog/ui";
import { fetchPublishedBlogPostBySlug } from "@/lib/blog/queries";
import { blogQueryKeys } from "@/lib/blog/query-keys";

const BlogPostPage = () => {
  const { slug = "" } = useParams();

  const { data: post, isLoading, isError } = useQuery({
    queryKey: blogQueryKeys.post(slug),
    queryFn: () => fetchPublishedBlogPostBySlug(slug),
    enabled: Boolean(slug),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container max-w-3xl py-16 md:py-24">
          <div className="h-8 w-48 bg-muted animate-pulse rounded mb-6" />
          <div className="h-12 w-full bg-muted animate-pulse rounded mb-4" />
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="h-4 bg-muted/70 animate-pulse rounded" />
            ))}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (isError || !post) {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead
          title="Article not found — LaunchGuide.io"
          description="This article could not be found."
          pathname={blogPostPath(slug)}
          noindex
        />
        <Navbar />
        <div className="container max-w-3xl py-20 text-center">
          <h1 className="text-3xl font-bold mb-3">Article not found</h1>
          <p className="text-muted-foreground mb-8">
            The article you are looking for may have been moved or is not published yet.
          </p>
          <Button asChild>
            <Link to={blogIndexPath()}>
              <ArrowLeft className="h-4 w-4 mr-2" aria-hidden />
              Back to Insights
            </Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const pathname = blogPostPath(post.slug);
  const title = resolveDocumentTitle(post);
  const description = resolveMetaDescription(post);
  const dateLabel = formatBlogDate(post.published_at || post.created_at);
  const keywords = post.keywords?.filter(Boolean) ?? [];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={title}
        description={description}
        pathname={pathname}
        keywords={keywords.length > 0 ? keywords : undefined}
        jsonLd={post.json_ld ?? undefined}
      />
      <Navbar />

      <article>
        <header className="relative border-b overflow-hidden">
          <div
            className="absolute inset-0 -z-10"
            style={{ background: BLOG_HERO_GRADIENT }}
          />
          <div className="container max-w-3xl py-12 md:py-16">
            <Link
              to={blogIndexPath()}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              All insights
            </Link>

            {post.tags && post.tags.length > 0 ? (
              <div className="flex flex-wrap gap-2 mb-5">
                {post.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="font-normal">
                    <Tag className="h-3 w-3 mr-1" aria-hidden />
                    {tag}
                  </Badge>
                ))}
              </div>
            ) : null}

            <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-balance leading-tight">
              {post.title}
            </h1>

            {post.excerpt ? (
              <p className="mt-5 text-lg md:text-xl text-muted-foreground text-balance leading-relaxed">
                {post.excerpt}
              </p>
            ) : null}

            {dateLabel ? (
              <p className="mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4" aria-hidden />
                {dateLabel}
              </p>
            ) : null}
          </div>
        </header>

        <div className="container max-w-3xl py-10 md:py-14 px-4 sm:px-6">
          <div
            className={BLOG_ARTICLE_PROSE_CLASS}
            dangerouslySetInnerHTML={{ __html: post.content_html }}
          />
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
